import { create } from 'zustand'
import { doubleApi } from '../services/doubleApi'
import useAppStore from '../../../store/useAppStore'

const PENDING_ITEM_LOCK_MS = 5000
let betFormRequestId = 0

const useDoubleStore = create((set, get) => ({
  // State
  activeRound: null,
  engine: null,
  isConnected: false,
  isModeDisabled: false,
  limits: null,
  isLoading: false,
  isBetting: false,
  error: null,
  inventory: [],
  balance: 0,
  pendingItemLocks: {},
  ownBetAcks: [],

  // Actions
  clearError: () => set({ error: null }),

  // Загрузка активного раунда для поллинга
  loadActiveRound: async (signal) => {
    try {
      set({ isLoading: true, error: null })
      const round = await doubleApi.getActiveRound(signal)

      if (round?.error) {
        const error = new Error(round.error)
        error.message = round.error
        throw error
      }

      set((state) => state.engine === 'node' && state.isConnected
        ? { isLoading: false }
        : { activeRound: round ? { ...round, received_at: Date.now() } : round, engine: round?.engine || 'worker', isLoading: false })

      return round
    } catch (error) {
      if (signal?.aborted) {
        set({ isLoading: false })
        throw error
      }
      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  },

  // Загрузка данных для формы ставки
  loadBetForm: async (signal) => {
    const requestId = ++betFormRequestId
    try {
      set({ isLoading: true, error: null })
      const data = await doubleApi.getBetForm(signal, () => get().engine === 'node')
      if (requestId !== betFormRequestId) return data

      if (data?.error) {
        const error = new Error(data.error)
        error.message = data.error
        throw error
      }

      set((state) => {
        const now = Date.now()
        const activeLocks = Object.fromEntries(
          Object.entries(state.pendingItemLocks || {}).filter(([, expiresAt]) => Number(expiresAt) > now)
        )

        const filteredInventory = (data.inventory || []).filter((item) => !activeLocks[item.id])

        return {
          inventory: filteredInventory,
          balance: state.engine === 'node' ? state.balance : Number(data.balance || 0),
          pendingItemLocks: activeLocks,
          isLoading: false
        }
      })

      return data
    } catch (error) {
      if (requestId !== betFormRequestId) throw error
      set({
        error: error.message || 'Инвентарь недоступен',
        isLoading: false
      })
      throw error
    }
  },

  // Отправка ставки
  placeBet: async ({ roundId, color, crystal, items }) => {
    try {
      set({ isBetting: true, error: null })

      const response = await doubleApi.placeBet({
        roundId,
        color,
        crystal,
        items
      })

      set((state) => {
        const selectedItemIds = new Set(Array.isArray(items) ? items : [])
        const nextInventory = (state.inventory || []).filter((item) => !selectedItemIds.has(item.id))
        const crystalValue = Number(crystal || 0)
        const currentBalance = Number(state.balance || 0)
        const lockExpiresAt = Date.now() + PENDING_ITEM_LOCK_MS
        const nextPendingItemLocks = { ...state.pendingItemLocks }

        selectedItemIds.forEach((itemId) => {
          nextPendingItemLocks[itemId] = lockExpiresAt
        })

        return {
          isBetting: false,
          inventory: nextInventory,
          balance: Math.max(0, currentBalance - (Number.isFinite(crystalValue) ? crystalValue : 0)),
          pendingItemLocks: nextPendingItemLocks
        }
      })
      return response
    } catch (error) {
      set({
        error: error.message || 'Ошибка отправки ставки',
        isBetting: false
      })
      throw error
    }
  },

  // Быстрая загрузка истории (лента раундов + ставки пользователя)
  loadHistoryFast: async () => {
    try {
      set({ isLoading: true, error: null })
      const data = await doubleApi.getHistoryFast()

      if (data?.error) {
        const error = new Error(data.error)
        error.message = data.error
        throw error
      }

      set({ isLoading: false })
      return {
        rounds: Array.isArray(data?.rounds) ? data.rounds : [],
        myBets: Array.isArray(data?.my_bets) ? data.my_bets : []
      }
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  // Загрузка обеих историй (полная, для страницы истории)
  loadHistory: async () => {
    try {
      set({ isLoading: true, error: null })
      const [all, mine] = await Promise.all([
        doubleApi.getHistoryAll(),
        useAppStore.getState().isGuest ? Promise.resolve([]) : doubleApi.getHistoryMine()
      ])

      set({ isLoading: false })
      return {
        all: Array.isArray(all) ? all : [],
        mine: Array.isArray(mine) ? mine : []
      }
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  // Загрузка конкретного раунда
  loadRoundById: async (roundId) => {
    try {
      set({ isLoading: true, error: null })
      const round = await doubleApi.getRoundById(roundId)
      set({ isLoading: false })
      return round
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  }
}))

export default useDoubleStore
